import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import driverAuthService from '../services/driverAuthService';

const DriverContext = createContext({});

export const useDriver = () => {
  const context = useContext(DriverContext);
  if (!context) {
    throw new Error('useDriver must be used within DriverProvider');
  }
  return context;
};

export const DriverProvider = ({ children }) => {
  const [driver, setDriver] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check current session
    checkDriver();

    // Listen for auth changes - FIXED: Use fire-and-forget pattern to prevent race conditions
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      console.log('🔄 Auth state change:', event, 'User:', session?.user?.email);
      
      if (event === 'SIGNED_IN' && session?.user) {
        // Fire-and-forget: Don't await async operations in callback
        loadDriverData(session?.user);
      } else if (event === 'SIGNED_OUT') {
        console.log('👋 User signed out, clearing driver state');
        setDriver(null);
        setEmployee(null);
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  const checkDriver = async () => {
    try {
      setLoading(true);
      const driverData = await driverAuthService?.getCurrentDriver();
      if (driverData) {
        console.log('✅ Driver session found:', driverData?.employee?.name);
        setDriver(driverData?.user);
        setEmployee(driverData?.employee);
      } else {
        console.log('❌ No driver session found');
      }
    } catch (error) {
      console.error('Error checking driver session:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDriverData = async (user) => {
    try {
      const { data: employeeData, error } = await supabase?.from('employees')?.select('*')?.eq('auth_user_id', user?.id)?.single();

      if (error || !employeeData) {
        console.log('⚠️ No employee record found - user may be admin or client');
        setDriver(null);
        setEmployee(null);
        return;
      }

      const mappedEmployee = {
        id: employeeData?.id,
        name: employeeData?.name,
        email: employeeData?.email,
        phone: employeeData?.phone,
        role: employeeData?.role,
        zone: employeeData?.zone,
        isActive: employeeData?.is_active
      };

      console.log('✅ Employee data loaded:', mappedEmployee?.name);
      setEmployee(mappedEmployee);
      setDriver(user);
    } catch (error) {
      console.error('Error loading driver data:', error);
      setDriver(null);
      setEmployee(null);
    }
  };

  const signIn = async (email, password) => {
    console.log('🔐 DriverContext signIn called for:', email);
    const result = await driverAuthService?.signIn(email, password);
    
    // Immediately update context state to prevent race condition
    console.log('📝 Updating context state immediately after sign in');
    setDriver(result?.user);
    setEmployee(result?.employee);
    
    return result;
  };

  const signOut = async () => {
    console.log('👋 DriverContext signOut called');
    await driverAuthService?.signOut();
    setDriver(null);
    setEmployee(null);
  };

  const value = {
    driver,
    employee,
    loading,
    signIn,
    signOut
  };

  return <DriverContext.Provider value={value}>{children}</DriverContext.Provider>;
};

export default DriverContext;